import { onAuthStateChanged, signInWithCustomToken, signOut, type Auth, type User } from 'firebase/auth'

// One sign-in for this app and the city (city.metafor.ai). Firebase keeps its
// session per origin, so the api keeps a session cookie for both
// (modules/auth_session): signing in here starts it, a tab that is signed out
// signs in from it, and signing out ends it, which the other app notices when
// its tab is next shown. The same file is in city-app-react
// (src/firebase/sharedSession.js).
//
// Every failure falls back to the app's own sign-in: an api without the
// endpoints, or one that is down, leaves things as they were.

const TIMEOUT_MS = 8000

export function createSharedSession(auth: Auth, apiBaseUrl: string) {
  const base = `${apiBaseUrl}/api/v1/auth/session`
  const call = (path: string, init: RequestInit = {}) =>
    fetch(base + path, { credentials: 'include', signal: AbortSignal.timeout(TIMEOUT_MS), ...init })

  // The uid this page last wrote the cookie for. Only then does a missing
  // cookie mean "signed out elsewhere" rather than "never started".
  let syncedUid: string | null = null

  let restoring = true
  const listeners = new Set<() => void>()
  const setRestoring = (value: boolean) => {
    restoring = value
    listeners.forEach((cb) => cb())
  }

  // A platform admin signed in as the user: that session stays in its app.
  const isImpersonated = async (user: User) => 'impersonated_by' in (await user.getIdTokenResult()).claims

  const sync = async (user: User) => {
    if (await isImpersonated(user)) return
    const res = await call('', { method: 'POST', headers: { Authorization: `Bearer ${await user.getIdToken()}` } })
    // read it back: a browser that blocks the cookie must not count as signed out
    const back = res.ok ? await call('') : null
    syncedUid = back?.ok && ((await back.json()) as { uid: string }).uid === user.uid ? user.uid : null
  }

  const restore = async () => {
    // the admin panel's "Sign in as" lands there with a token of its own
    if (window.location.pathname === '/impersonate') return
    const res = await call('/token', { method: 'POST' })
    if (!res.ok) return
    const { token } = (await res.json()) as { token: string }
    await signInWithCustomToken(auth, token)
  }

  const check = async () => {
    if (restoring) return
    const user = auth.currentUser
    if (!user) return restore()
    if (syncedUid !== user.uid) return sync(user)
    const res = await call('')
    if (res.status === 401) {
      syncedUid = null
      await signOut(auth)
    } else if (res.ok && ((await res.json()) as { uid: string }).uid !== user.uid) {
      // signed in as someone else in the other app
      syncedUid = null
      await signOut(auth)
      await restore()
    }
  }

  onAuthStateChanged(auth, (user) => {
    if (!user) syncedUid = null
    else if (user.uid !== syncedUid) sync(user).catch(() => {})
  })

  auth
    .authStateReady()
    .then(() => (auth.currentUser ? null : restore()))
    .catch(() => {})
    .finally(() => setRestoring(false))

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check().catch(() => {})
  })

  return {
    // True until the first sign-in from the cookie has been tried: guards wait
    // for it before sending a signed-out user to /login.
    restoringStore: {
      subscribe: (cb: () => void) => (listeners.add(cb), () => listeners.delete(cb)),
      get: () => restoring,
    },
    // Before signOut: or the next page load would sign straight back in.
    end: async () => {
      syncedUid = null
      const user = auth.currentUser
      // ending an impersonation leaves the real sign-in alone
      if (user && (await isImpersonated(user).catch(() => false))) return
      await call('', { method: 'DELETE' }).catch(() => {})
    },
  }
}
